import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Map } from 'lucide-react';
import VoucherGuestsList from './VoucherGuestsList';
import { formatBookingId } from '../../../utils/trackingHelpers';

const ActiveVoucherRow = memo(({
  voucher,
  activeSharingInfo,
  isExpanded,
  onToggle,
  guests,
  loadingGuests,
  onPing
}) => {
  const navigate = useNavigate();
  const activeCount = Number(activeSharingInfo?.activeCount) || 0;

  return (
    <React.Fragment>
      <tr
        className={`group transition-all duration-300 ${
          isExpanded ? 'bg-primary/5' : 'hover:bg-slate-50'
        }`}
      >
        <td
          className={`py-4 px-4 rounded-l-2xl border-y border-l border-slate-100 transition-colors ${
            isExpanded ? 'border-primary/20' : ''
          }`}
        >
          <div className="flex flex-col">
            <span className="font-bold text-slate-800 text-sm">
              {voucher.clientName}
            </span>
            <span className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">
              {formatBookingId(voucher.bookingID)}
            </span>
          </div>
        </td>
        <td
          className={`py-4 border-y border-slate-100 transition-colors ${
            isExpanded ? 'border-primary/20' : ''
          }`}
        >
          <span className="text-xs text-slate-600 font-semibold">
            {voucher.destination || 'N/A'}
          </span>
        </td>
        <td
          className={`py-4 border-y border-slate-100 transition-colors ${
            isExpanded ? 'border-primary/20' : ''
          }`}
        >
          <div className="flex flex-col gap-1 items-start">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                voucher.status === 'completed'
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                  : voucher.status === 'onTrip'
                  ? 'bg-blue-50 text-blue-600 border-blue-100'
                  : voucher.status === 'installed'
                  ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                  : 'bg-rose-50 text-rose-600 border-rose-100'
              }`}
            >
              {voucher.status}
            </span>
            {activeCount > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                {activeCount} sharing location
              </span>
            )}
          </div>
        </td>
        <td
          className={`py-4 px-4 rounded-r-2xl border-y border-r border-slate-100 transition-colors text-right ${
            isExpanded ? 'border-primary/20' : ''
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => onToggle(voucher._id, voucher.bookingID)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                isExpanded
                  ? 'bg-slate-100 text-slate-800 border-slate-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              View Guests
            </button>
            <button
              onClick={() => navigate(`/tracking/map?voucherId=${voucher.bookingID}`)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-primary border border-primary/25 rounded-lg hover:bg-primary/5 transition-all cursor-pointer"
            >
              <Map size={12} />
              {activeCount > 0 ? 'Track Location' : 'Track Route'}
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded Guest Info */}
      {isExpanded && (
        <tr>
          <td colSpan="4" className="py-4 px-2">
            <VoucherGuestsList
              voucher={voucher}
              guests={guests}
              loadingGuests={loadingGuests}
              onPingAll={() => onPing(voucher.bookingID, 'all')}
              onPingGuest={(guestName) => onPing(voucher.bookingID, guestName)}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
});

ActiveVoucherRow.displayName = 'ActiveVoucherRow';

export default ActiveVoucherRow;
